/**
 * A player's avatar artwork. SPEC.md §46, §19, §39.
 *
 * One component for all of it, because the alternative is ten `<img>` tags that
 * each decide separately how to resolve a key, whether to lazy-load, and what
 * to announce — and the one that decides differently is the one that ships a
 * broken image or reads the character's name out in the middle of a score. §60
 *
 * It is always **decorative**: `alt=""` and aria-hidden. Every place an avatar
 * appears, the player's name is already next to it in the accessibility tree, so
 * an alt text here would make a screen reader say "Pikachu, Mike, plus twelve".
 * The one surface where the artwork *is* the information — the picker, where the
 * choice has no name beside it — labels its own button instead. §19
 *
 * It loads eagerly by default and defers only where a caller asks, because the
 * screen that matters most — the ring of seats — is the one where every avatar
 * is already on screen and a deferred fetch is pure latency. §18.2
 *
 * It carries no size of its own. Tailwind emits utilities in its own order
 * rather than the order they appear in a class attribute, so a `size-*` baked in
 * here would win or lose against a caller's `size-*` depending on which one
 * Tailwind happened to write first — a coin toss that is invisible until a seat
 * renders at the wrong size. The caller sizes the box; this fills it. §45
 */

import type { CSSProperties } from 'react';
import { AVATAR_IMAGE_PIXELS, avatarSrc } from '@card-counter/shared';

export interface AvatarProps {
  /** A player's stored key. Unknown keys resolve to the fallback. §46 */
  avatarKey: string;
  /** Sizing for the image — `size-8`, `size-[86%]`, whatever the site needs. */
  className?: string;
  style?: CSSProperties;
  /**
   * Defer until it scrolls near the viewport. For the picker's grid and nothing
   * else: it draws all 112 at once inside a box that shows about eight, and a
   * 224 px sticker costs ~200 KB of bitmap once decoded — eagerly, the set is
   * some 22 MB of decode on a phone, to choose one face.
   *
   * Never on a seat or a lobby tile. Those are above the fold on the screen that
   * must not reflow, and a lazy image is not fetched until layout has run. §18.2
   */
  defer?: boolean;
}

export function Avatar({ avatarKey, className, style, defer = false }: AvatarProps) {
  return (
    <img
      // Resolves the fallback itself, so an unknown key asks for a file that
      // exists rather than rendering a broken-image glyph. §46
      src={avatarSrc(avatarKey)}
      alt=""
      aria-hidden="true"
      // The artwork's intrinsic size, so the box is reserved before the bytes
      // arrive and the row does not reflow around a late image. §18.2
      width={AVATAR_IMAGE_PIXELS}
      height={AVATAR_IMAGE_PIXELS}
      loading={defer ? 'lazy' : 'eager'}
      // The picker's tiles are the only avatars a player is not already looking
      // at, so they are the only ones that may queue behind the shell.
      fetchPriority={defer ? 'low' : 'auto'}
      decoding="async"
      /*
        A seat is a drag handle. The browser's native image drag starts on
        pointerdown and hands the gesture to the OS, so dnd-kit sees the seat
        picked up and then never sees it move — the reorder silently does
        nothing on desktop. §9.3
      */
      draggable={false}
      /*
        `pointer-events: none`, so the artwork is never what a finger lands on. §9.3

        A seat is dragged by a long press, and a long press on an `<img>` is a
        gesture the browser has its own plans for: iOS raises the Save Image
        callout, Android opens the image context menu, and both take the touch
        away mid-press. The seat's own `onContextMenu` cannot save it — iOS
        raises the callout without ever firing `contextmenu`, and by then the
        pointer stream dnd-kit is following has already ended. So the avatar
        stops being a hit target at all and the press lands on the seat card
        behind it, which is not an image and has nothing to offer.

        Safe everywhere precisely because this component is decorative wherever
        it appears (see the note above): every avatar in the app sits inside the
        button, seat or row that actually handles the tap. The `-webkit-*`
        properties in index.css's base layer are the belt to this pair of braces.
      */
      className={['pointer-events-none block object-contain select-none', className]
        .filter(Boolean)
        .join(' ')}
      style={style}
    />
  );
}
